import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Leaf } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "../../hooks/use-toast";
import ConfirmAlert from "../../components/ui/ConfirmAlert";
import api from "../../services/api";

const AdminCategories = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name_en: "",
    name_ar: "",
  });
  const [editCategory, setEditCategory] = useState({
    id: "",
    name_en: "",
    name_ar: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  /* ---------------- LOAD CATEGORIES ---------------- */
  const loadCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (err) {
      setError(t("admin.categories.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  /* ---------------- ADD CATEGORY ---------------- */
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post("/categories", newCategory);

      toast({
        title: t("admin.categories.addSuccess"),
      });

      setShowAddForm(false);
      setNewCategory({ name_en: "", name_ar: "" });
      loadCategories();
    } catch (err) {
      toast({
        title: t("admin.errorTitle"),
        description: t("admin.categories.addError"),
        variant: "destructive",
      });
    } finally {
      setShowAddForm(false);
      setNewCategory("");
    }
  };

  /* ---------------- OPEN EDIT FORM ---------------- */
  const openEditForm = (category) => {
    setEditCategory({
      id: category.id,
      name_en: category.name_en,
      name_ar: category.name_ar,
    });
    setShowEditForm(true);
  };

  /* ---------------- EDIT CATEGORY ---------------- */
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post(`/categories/edit/${editCategory.id}`, editCategory);

      toast({
        title: t("admin.categories.updateSuccess"),
      });

      setShowEditForm(false);
      loadCategories();
    } catch (err) {
      toast({
        title: t("admin.errorTitle"),
        description: t("admin.categories.updateError"),
        variant: "destructive",
      });
    }
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditCategory({ id: "", name_en: "", name_ar: "" });
  };

  /* ---------------- DELETE CATEGORY ---------------- */
  const requestDelete = (id) => {
    setSelectedUserId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;
    try {
      await api.post(`/categories/delete/${selectedUserId}`);

      toast({
        title: t("admin.categories.deleteSuccess"),
      });

      loadCategories();
    } catch (err) {
      // If backend returns 400 with product warning
      if (
        err.response?.status === 400 &&
        err.response?.data?.productCount > 0
      ) {
        const productCount = err.response.data.productCount;
        const confirmForce = window.confirm(
          t("admin.categories.forceDeleteConfirm", {
            count: productCount,
          }),
        );

        if (confirmForce) {
          try {
            await api.post(`/categories/delete/${selectedUserId}`, {
              force: true,
            });

            toast({
              title: t("admin.categories.forceDeleteSuccessTitle"),
              description: t("admin.categories.forceDeleteSuccessDesc", {
                count: productCount,
              }),
            });

            loadCategories();
          } catch (forceErr) {
            toast({
              title: t("admin.categories.deleteFailedTitle"),
              description:
                forceErr.response?.data?.message ||
                t("admin.categories.deleteFailed"),
              variant: "destructive",
              duration: 5000,
            });
          }
        }
      } else {
        toast({
          title: t("admin.categories.cannotDeleteTitle"),
          description:
            err.response?.data?.message || t("admin.categories.cannotDelete"),
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setConfirmOpen(false);
      setSelectedUserId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground mb-2">
            {t("admin.categories.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("admin.categories.subtitle")}
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-hero flex items-center gap-2 px-5 py-3 text-sm"
        >
          <Plus size={18} />
          <span>{t("admin.categories.addButton")}</span>
        </button>
      </div>

      {/* ---------------- ADD MODAL ---------------- */}
      {showAddForm && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-large max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-serif font-semibold">
                {t("admin.categories.addTitle")}
              </h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t("admin.categories.nameEn")}
                </label>
                <input
                  type="text"
                  value={newCategory.name_en}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name_en: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  placeholder="e.g., Seeds"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t("admin.categories.nameAr")}
                </label>
                <input
                  type="text"
                  value={newCategory.name_ar}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name_ar: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  placeholder="مثال: بذور"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="flex-1 btn-hero">
                  {t("admin.categories.addButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditForm && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-large max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-serif font-semibold">
                {t("admin.categories.editTitle")}
              </h3>
              <button
                onClick={() => setShowEditForm(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t("admin.categories.nameEn")}
                </label>
                <input
                  type="text"
                  value={editCategory.name_en}
                  onChange={(e) =>
                    setEditCategory({
                      ...editCategory,
                      name_en: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t("admin.categories.nameAr")}
                </label>
                <input
                  type="text"
                  value={editCategory.name_ar}
                  onChange={(e) =>
                    setEditCategory({
                      ...editCategory,
                      name_ar: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeEditForm}
                  className="flex-1 px-4 py-3 rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="flex-1 btn-hero">
                  {t("admin.categories.editButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      {loading && <p className="text-center">Loading categories...</p>}
      {error && <p className="text-center text-red-500">{error}</p>}

      {!loading && !error && categories.length === 0 && (
        <div className="text-center py-16">
          <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">
            No categories available at the moment.
          </p>
        </div>
      )}
      {!loading && !error && categories.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-card p-6 rounded-2xl shadow-soft flex items-center justify-between"
            >
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {lang === "ar" ? category.name_ar : category.name_en}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {category.products_count} {t("admin.categories.products")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditForm(category)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors text-foreground"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => requestDelete(category.id)}
                  className="p-2 hover:bg-destructive/10 rounded-lg transition-colors text-destructive"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {confirmOpen && (
            <ConfirmAlert
              open={confirmOpen}
              title={t("admin.categories.deleteTitle")}
              description={t("admin.categories.deleteConfirm")}
              onConfirm={handleDelete}
              onCancel={() => setConfirmOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
